"use client"

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { SlidePanel } from '../ui/slide-panel';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { useUpdateDepartment, useGetDepartmentById } from '../../api/department/departmentQueries';
import { Textarea } from '../ui/textarea.tsx';

export function DepartmentEditPanel({ department, isOpen, onClose }) {
  const { mutate: updateDepartment, isPending: isUpdating } = useUpdateDepartment();
  const deptId = department?.id || department?.deptId;
  const { data: departmentData, isLoading: isLoadingDepartment } = useGetDepartmentById(deptId);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (departmentData) {
      // Handle data from API
      setName(departmentData.name || departmentData.departmentName || '');
      setDescription(departmentData.description || '');
    } else if (department) {
      // Handle data from direct prop
      setName(department.name || department.departmentName || '');
      setDescription(department.description || '');
    }
    // Clear any previous errors when department data changes
    setErrors({});
    
    // Log what we're using for debugging
    console.log('Department Data for Edit:', departmentData || department);
  }, [department, departmentData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!deptId) {
      newErrors.id = 'Department ID is required';
      console.error('Missing department ID for update operation');
      toast.error('Cannot update department: Missing ID');
      return false;
    }
    
    if (!name || !name.trim()) {
      newErrors.name = 'Department name is required';
    }

    if (!description || !description.trim()) {
      newErrors.description = 'Department description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await updateDepartment({
        departmentId: deptId,
        data: {
          name: name.trim(),
          description: description.trim()
        }
      });
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to update department');
    }
  };

  if (isLoadingDepartment) {
    return (
      <SlidePanel isOpen={isOpen} onClose={onClose}>
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </SlidePanel>
    );
  }

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose}>
      <div className="space-y-4 p-4">
        <h2 className="text-lg font-semibold">Edit Department</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex justify-between">
              <span>Department Name</span>
              {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder="Enter department name"
              className={errors.name ? 'border-red-500' : ''}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="flex justify-between">
              <span>Description</span>
              {errors.description && <span className="text-sm text-red-500">{errors.description}</span>}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: '' }));
              }}
              placeholder="Enter department description"
              rows={4}
              className={errors.description ? 'border-red-500' : ''}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? <LoadingSpinner className="mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </SlidePanel>
  );
}